// DS: STU3.6.1
import type { FC } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiGallection } from '@wix/da-papi-types';
import Dumbo from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import ImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddImage';
import LockIcon from '@wix/da-ds/pkg/Icons/24x24/Lock';
import SubfoldersIcon from '@wix/da-ds/pkg/Icons/24x24/Subfolders';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import FolderRibbon from '@wix/da-shared-react/pkg/Primitives/FolderRibbon';
import BaseCard from '../BaseCard';
import FolderActionsDropdown from '../FolderActionsDropdown';

import s from './FolderCard.scss';

export interface Props {
  className?: string;
  folder: PapiGallection;
  width: number;
  height: number;
  withActions?: boolean;
  biData?: any;
  innerRef?: any;
  showLockIcon?: boolean;
}

const getFolderHref = (folder: PapiGallection) => {
  if (folder.type === 'stash') {
    return Url.studioStash(folder.folderId);
  } else if (folder.type === 'gallery') {
    return Url.studioGallery(folder.folderId);
  }
};

const FolderCard: FC<Props> = ({
  className,
  folder,
  width,
  height,
  withActions,
  biData,
  innerRef,
  showLockIcon,
}) => {
  const { t } = useTranslation();
  const count = folder.totalItemCount ?? folder.size ?? 0;

  return (
    <BaseCard
      innerRef={innerRef}
      className={cn(s['root'], className)}
      width={width}
      height={height}
      biData={biData}
      complexLabel
      thumb={
        folder.thumb && !folder.thumb.isDefaultImage ? (
          <Dumbo deviation={folder.thumb} height={height} width={width} />
        ) : (
          <div className={s['nopreview']}>
            <ImageIcon size={IconSize.MEDIUM} />
          </div>
        )
      }
      ribbon={
        <FolderRibbon
          premiumFolderData={folder.premiumFolderData}
          className={s['ribbon']}
        />
      }
      icons={folder.hasSubfolders ? <SubfoldersIcon /> : undefined}
      label={(
        <div className={s['complex-label']}>
          <span className={s['label-name']}>{folder.name}</span>
          <span className={s['label-size']}>
            {t('common.deviationCount', { count })}
          </span>
        </div>
      )}
      href={getFolderHref(folder)}
      actionsWrapperClassName={s['actions-wrapper']}
      actions={[
        showLockIcon && (
          <LockIcon className={s['lock-icon']} size={IconSize.SMALLER} />
        ),
        withActions && (
          <FolderActionsDropdown
            key="actions"
            className={s['actions']}
            folder={folder}
            sizeOverride="small"
            withCreateFolder={false}
            withBookmark
          />
        ),
      ]}
    />
  );
};
FolderCard.displayName = 'FolderCard';

export default FolderCard;
